/**
 * Working Alert for the web bundle.
 *
 * react-native-web ships `Alert.alert() {}` - an empty function - so every
 * shared `Alert.alert(...)` call silently does nothing in a browser. The one
 * that matters most is the interceptor's "Session Timeout!" on a 401: on web
 * the session ended with no explanation at all.
 *
 * This maps the React Native API onto the browser's own dialogs:
 *   - no buttons / one button    -> window.alert, then that button's onPress
 *   - two or more buttons        -> window.confirm: OK runs the last
 *                                   non-cancel button, Cancel runs the cancel
 *                                   button (or the first one)
 *
 * Several requests failing at once (session restore fires five) would each
 * raise the same alert back to back, so an identical alert raised within
 * BURST_MS of the previous one closing is dropped.
 *
 * Web only: swapped in for react-native-web's Alert by webpack.config.js.
 * Android keeps the native Alert.
 */

type AlertButton = {
  text?: string;
  onPress?: (value?: string) => void;
  style?: 'default' | 'cancel' | 'destructive';
};

const BURST_MS = 1500;

let lastText = '';
let lastClosedAt = 0;

class Alert {
  static alert(
    title?: string,
    message?: string,
    buttons?: AlertButton[],
    _options?: unknown,
  ): void {
    const text = [title, message].filter(Boolean).join('\n\n');

    if (text === lastText && Date.now() - lastClosedAt < BURST_MS) {
      return;
    }

    let chosen: AlertButton | undefined;
    if (!buttons || buttons.length < 2) {
      window.alert(text);
      chosen = buttons?.[0];
    } else {
      const cancel = buttons.find(b => b.style === 'cancel') ?? buttons[0];
      const confirm =
        [...buttons].reverse().find(b => b !== cancel) ?? buttons[buttons.length - 1];
      chosen = window.confirm(text) ? confirm : cancel;
    }

    lastText = text;
    lastClosedAt = Date.now();
    chosen?.onPress?.();
  }

  static prompt(): void {}
}

export default Alert;
